import type { CityConfig } from "@/pages/city-template/types";

export const manhattanConfig: CityConfig = {
  city: "Manhattan",
  state: "Kansas",
  stateAbbr: "KS",
  slug: "manhattan",
  route: "/manhattan",
  title: "Emergency Passport Manhattan, KS — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Manhattan, KS — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Manhattan and Riley County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Manhattan, Kansas",
  heroSubtitle: "Need an urgent passport in the Flint Hills? Fast Passport Center helps Manhattan travelers — including K-State students, faculty, and Fort Riley families — get their passports fast, as fast as 24 hours. No need to drive to a regional agency. A+ BBB rated, registered State Dept. courier.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Manhattan%20Kansas%20Flint%20Hills%20landscape%20with%20Kansas%20State%20University%20campus%20limestone%20buildings%20in%20distance%2C%20rolling%20tallgrass%20prairie%20hills%20at%20golden%20hour%2C%20warm%20amber%20and%20wheat%20tones%2C%20Tuttle%20Creek%20Lake%20shimmering%2C%20small%20college%20town%20skyline%20against%20big%20prairie%20sky%2C%20photorealistic%20landscape&width=1800&height=700&seq=manhattan-hero-01&orientation=landscape",
  heroImageAlt: "Manhattan Kansas Flint Hills landscape with Kansas State University campus and rolling prairie at golden hour",
  geoPosition: "39.1836;-96.5717",
  geoPlacename: "Manhattan, Kansas",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Denver Passport Agency",
    address: "3151 S Vaughn Way, Suite 600, Aurora, CO 80014",
    distance: "Approximately 490 miles west — nearest regional passport agency for Flint Hills and northeast Kansas travelers",
  },
  nearbyCitiesZips: [
    { name: "Manhattan", zips: "66502–66506" },
    { name: "Junction City", zips: "66441–66442" },
    { name: "Fort Riley", zips: "66442" },
    { name: "Wamego", zips: "66547" },
    { name: "Ogden", zips: "66517" },
    { name: "Riley", zips: "66531" },
    { name: "St. George", zips: "66535" },
    { name: "Alma", zips: "66401" },
    { name: "Abilene", zips: "67410" },
    { name: "Topeka", zips: "66601–66699" },
    { name: "Salina", zips: "67401–67402" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or your K-State dorm in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Manhattan", desc: "FedEx and UPS locations throughout Manhattan and the K-State campus area offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — especially important for last-minute passport needs." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport center — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Ryan T.",
      location: "Manhattan, KS",
      rating: 5,
      text: "I'm a K-State grad student and got a last-minute research conference invitation in London. My passport had expired and I had 6 days until my flight. Fast Passport Center got me a new passport in 4 days. Shipped from the FedEx on Anderson Ave, had it back before my Wednesday flight. Absolutely saved my academic career moment.",
      service: "Emergency Passport",
    },
    {
      name: "Jennifer H.",
      location: "Manhattan, KS",
      rating: 5,
      text: "Military spouse at Fort Riley — my husband got orders to Germany and we needed passports for the whole family within two weeks. Fast Passport Center handled all four of our applications, including our two young kids. Everything came back on time and we made the move without a hitch. Incredible service for military families.",
      service: "Family Passports",
    },
    {
      name: "Mark D.",
      location: "Junction City, KS",
      rating: 5,
      text: "Drove into Manhattan from Junction City to ship my documents and it was the smoothest passport experience I've had. Needed a renewal for a business trip to Tokyo. Fast Passport Center had my new passport in 5 business days. No driving to Arkansas or waiting weeks at the post office. Highly recommend for anyone in the Flint Hills area.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "manhattan-main-post",
      name: "Manhattan Post Office",
      type: "USPS Passport Acceptance",
      address: "500 Leavenworth St",
      city: "Manhattan",
      state: "KS",
      zip: "66502",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Manhattan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Main passport acceptance facility serving Manhattan and Riley County. Appointment required — book online well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "junction-city-post",
      name: "Junction City Post Office",
      type: "USPS Passport Acceptance",
      address: "302 W 7th St",
      city: "Junction City",
      state: "KS",
      zip: "66441",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–3:30pm",
      distance: "Junction City — about 20 miles southwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Convenient alternative for Junction City and Fort Riley residents. Smaller facility — appointments strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "riley-county-clerk",
      name: "Riley County Clerk's Office",
      type: "County Passport Acceptance",
      address: "110 Courthouse Plaza",
      city: "Manhattan",
      state: "KS",
      zip: "66502",
      phone: "785-537-6300",
      hours: "Mon–Fri 8am–5pm",
      distance: "Downtown Manhattan",
      website: "https://www.rileycountyks.gov/",
      websiteLabel: "Riley County Website",
      notes: "Riley County Clerk may offer passport acceptance services. Call ahead to confirm availability as services can vary by county.",
      icon: "ri-government-line",
    },
    {
      id: "wamego-post",
      name: "Wamego Post Office",
      type: "USPS Passport Acceptance",
      address: "806 4th St",
      city: "Wamego",
      state: "KS",
      zip: "66547",
      phone: "800-275-8777",
      hours: "Mon–Fri 8:30am–3pm",
      distance: "Wamego — about 15 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "East side option for Wamego, St. George, and eastern Pottawatomie County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "topeka-post",
      name: "Topeka Main Post Office",
      type: "USPS Passport Acceptance",
      address: "424 S Kansas Ave",
      city: "Topeka",
      state: "KS",
      zip: "66603",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Topeka — about 55 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Larger facility in the capital city, good fallback option if Manhattan appointments are full.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Manhattan before international travel?",
      a: "Yes. Through Fast Passport Center, Manhattan travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today.",
    },
    {
      q: "How fast can I renew my passport in Manhattan?",
      a: "Fast passport renewal in Manhattan is available through Fast Passport Center in as little as 3 business days with our expedited service. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "Does Manhattan have its own passport agency?",
      a: "Manhattan, Kansas does not have a regional passport agency. The closest regional passport center is the Denver Passport Agency in Hot Springs, Arkansas (about 430 miles southeast). Fast Passport Center is the faster and more convenient solution — no 6+ hour road trip required.",
    },
    {
      q: "I'm a K-State student — can I get a passport fast for a study abroad program?",
      a: "Absolutely. Many K-State students use Fast Passport Center for study abroad and international research trips. We understand student timelines and can expedite your passport in as little as 2–9 business days. Ship from campus-area FedEx or UPS locations in Manhattan.",
    },
    {
      q: "What about military families at Fort Riley needing passports for PCS overseas?",
      a: "Military families stationed at Fort Riley frequently use our services for PCS orders to Germany, Korea, Italy, and other overseas assignments. We can expedite passports for the entire family on tight military timelines. Government orders are not required — we process standard civilian passports.",
    },
    {
      q: "Can I expedite a child passport in Manhattan?",
      a: "Yes. Child passport expediting in Manhattan is possible through Fast Passport Center, but child passports for minors 15 and under require an in-person acceptance step with parental consent. We provide a clear checklist and instructions for Manhattan and Riley County families.",
    },
    {
      q: "What airports do Manhattan passport holders typically fly from?",
      a: "Manhattan Regional Airport (MHK) offers daily flights to Chicago O'Hare (ORD) and Dallas/Fort Worth (DFW) via American Eagle. For international travel, most Manhattan residents fly from Kansas City International (MCI) about 2 hours east, or Wichita Eisenhower National (ICT) about 1.5 hours south.",
    },
    {
      q: "Can I get a passport renewal near me in Manhattan without visiting an office?",
      a: "Yes. Most Manhattan adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in Manhattan.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Manhattan", address: "1300 Westloop Pl, Manhattan, KS 66502", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Manhattan", address: "3019 Anderson Ave, Manhattan, KS 66503", hours: "Daily 8 AM – 9 PM" },
    { name: "FedEx Office — Manhattan", address: "619 N Manhattan Ave, Manhattan, KS 66502", hours: "Mon–Fri 8 AM – 6 PM, Sat 9 AM – 3 PM" },
    { name: "Walmart — Manhattan", address: "101 Bluemont Ave, Manhattan, KS 66502", hours: "Daily 6 AM – 11 PM" },
  ],
  dmvLocations: [
    {
      name: "Manhattan Driver License Office",
      address: "322 Houston St, Manhattan, KS 66502",
      hours: "Mon–Fri 7 AM – 5:45 PM",
      note: "Main DMV office serving Riley County. Driver licenses, REAL ID, ID cards. Appointment recommended for shorter wait times.",
      featured: true,
    },
    {
      name: "Junction City Driver License Office",
      address: "814 N Washington St, Junction City, KS 66441",
      hours: "Tue–Fri 7 AM – 5:45 PM",
      note: "Alternative DMV for Junction City and Geary County residents. Closed Mondays.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "MHK",
      name: "Manhattan Regional Airport",
      distance: "Regional airport — daily American Eagle flights to ORD and DFW",
      address: "5500 Skyway Dr, Manhattan, KS 66503",
      note: "MHK offers daily connecting flights to Chicago O'Hare and Dallas/Fort Worth. Most international travelers connect through these hubs or drive to MCI.",
      flightUrl: "https://www.flymhk.com/",
      flightLabel: "MHK Airport Website",
      featured: true,
    },
    {
      code: "MCI",
      name: "Kansas City International Airport",
      distance: "Full international service — about 120 miles east (2 hours)",
      address: "1 International Sq, Kansas City, MO 64153",
      note: "MCI offers the most international flight options for Manhattan travelers. Direct flights to Europe, Mexico, and the Caribbean available.",
      flightUrl: "https://www.flykci.com/",
      flightLabel: "MCI Airport Website",
      featured: false,
    },
    {
      code: "ICT",
      name: "Wichita Dwight D. Eisenhower National Airport",
      distance: "About 150 miles south (2.5 hours)",
      address: "2277 Eisenhower Airport Pkwy, Wichita, KS 67209",
      note: "ICT is an alternative major airport for Manhattan travelers with domestic and some international connections.",
      flightUrl: "https://www.flywichita.com/",
      flightLabel: "ICT Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from MHK or MCI in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Manhattan and the Flint Hills.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Manhattan and Riley County travelers renew fast — expired passport before a trip is one of our most common urgent requests.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Manhattan", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Manhattan travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Manhattan and Fort Riley families — minors under 16 require in-person acceptance and parental consent.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Manhattan residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with the same expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Manhattan, Kansas travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Manhattan residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Manhattan, KS residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Manhattan travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Manhattan and Fort Riley families with minors under 16." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Manhattan travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Manhattan runs 6–13 weeks. If your trip is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Manhattan travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Manhattan", description: "Drop off at any FedEx or UPS in Manhattan — most locations are open weekdays and Saturdays. K-State campus area locations available." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you." },
  ],
  processingOptions: [
    {
      speed: "2–9", unit: "Business Days", label: "Fast Passport Center",
      tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Document review included", "Dedicated agent assigned", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5", unit: "Weeks", label: "Fast Passport Center",
      tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Document review included"],
      highlight: false, smartSaver: true,
    },
    {
      speed: "1–2", unit: "Months", label: "Self-Service (Post Office)",
      tag: "Slowest", tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access", "In-person clerk appointment required", "No document review", "No dedicated agent", "Not suitable if travel is within 10 weeks"],
      highlight: false, muted: true,
    },
    {
      speed: "24", unit: "Hours*", label: "Regional Agency Processing",
      tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required", "Life-or-death emergencies only", "Expect to wait all day or return next day"],
      highlight: false, emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots at Denver Passport Agency", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Hot Springs, AR (6.5 hours)", fpc: "Ship documents overnight from Manhattan" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
    { feature: "Turnaround Predictability", agency: "Depends on appointment availability — no guarantee", fpc: "Guaranteed processing within selected timeframe" },
    { feature: "Customer Support", agency: "Government clerk — limited availability", fpc: "Dedicated agent assigned to your case with daily updates" },
    { feature: "Travel Required", agency: "Yes — must travel to Denver in person", fpc: "No — ship documents from home or office in Manhattan" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Topeka", route: "/topeka" },
    { name: "Fast passport renewal in Salina", route: "/salina" },
    { name: "Emergency passport help near Junction City area", route: "/kansas" },
    { name: "Fast passport services for the Flint Hills region", route: "/kansas" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Manhattan — including locations near the K-State campus. Most are open weekdays and Saturdays. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Manhattan and all of Riley County remotely",
  serviceAreaIntro: "Whether you are in Manhattan, Junction City, Fort Riley, Wamego, or anywhere in Riley County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Manhattan and Riley County zip codes. Anywhere in the Flint Hills — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Manhattan as simple as possible. Most Manhattan clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Manhattan and Riley County travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from Manhattan, Junction City, and Flint Hills travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Manhattan, K-State, and Riley County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/kansas",
  statePageName: "Kansas",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Kansas Department of Health and Environment Vital Statistics. Online, mail, or in-person options available for Riley County residents.", url: "https://www.kdhe.ks.gov/" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through KDHE Vital Statistics. Required for estate, insurance, and legal matters.", url: "https://www.kdhe.ks.gov/" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Riley County District Court. Certified copies available for name changes and legal purposes.", url: "https://www.rileycountyks.gov/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Riley County District Court. Required for name change passport corrections.", url: "https://www.rileycountyks.gov/" },
  ],
};